import * as XLSX from 'xlsx';
import { formatCurrency } from './format';

// Formatos numéricos reutilizables
const FMT_MONEY = '$#,##0';
const FMT_MONEY_DEC = '$#,##0.00';
const FMT_PCT = '0"%"';
const FMT_INT = '#,##0';
const FMT_DATE = 'dd/mm/yyyy';

/**
 * Genera y descarga un Excel con el detalle completo de la liquidación.
 * Hojas:
 *   - Resumen              (datos del cliente, liquidación, totales y desglose por tipo)
 *   - Kardex               (prenda × entregas con badge de tipo y totales)
 *   - Precios y Subtotales  (prenda con cantidad por tipo + precio final editable)
 *   - Detalle de Entregas  (auditoría con desglose de recargo)
 *
 * Archivo: `liquidacion_{consecutivo}_{cliente}_{YYYYMMDD}.xlsx`
 */
export const exportarLiquidacionExcel = ({ liquidacion }) => {
  if (!liquidacion) return;

  const entregas = [...(liquidacion.entregas || [])].sort(
    (a, b) =>
      new Date(a.fechaEntrega || a.createdAt) -
      new Date(b.fechaEntrega || b.createdAt),
  );

  // Helpers
  const TIPO_LABEL = { ordinaria: 'Ord', dominical: 'Dom', extra: 'Extra' };
  const tipoOf = (e) => e.tipoEntrega || 'ordinaria';
  const isDom = (e) => tipoOf(e) === 'dominical';
  const isExt = (e) => tipoOf(e) === 'extra';

  // Prendas agregadas + matriz { [prendaId]: { [entregaId]: cantidad } }
  // + cantidades por tipo por prenda
  const prendasMap = new Map();
  const cantPorEnt = {};
  const cantPorTipoPorPrenda = {}; // { [prendaId]: { ordinaria, dominical, extra } }
  const subtPorTipoPorPrenda = {}; // { [prendaId]: { ordinaria, dominical, extra } }

  entregas.forEach((e) => {
    const tipo = tipoOf(e);
    (e.items || []).forEach((it) => {
      if (!prendasMap.has(it.prendaId)) {
        prendasMap.set(it.prendaId, {
          prendaId: it.prendaId,
          nombre: it.nombre,
          cantidad: 0,
        });
        cantPorTipoPorPrenda[it.prendaId] = { ordinaria: 0, dominical: 0, extra: 0 };
        subtPorTipoPorPrenda[it.prendaId] = { ordinaria: 0, dominical: 0, extra: 0 };
      }
      const cant = it.cantidad || 0;
      prendasMap.get(it.prendaId).cantidad += cant;
      cantPorEnt[it.prendaId] = cantPorEnt[it.prendaId] || {};
      cantPorEnt[it.prendaId][e.entregaId] = (cantPorEnt[it.prendaId][e.entregaId] || 0) + cant;
      cantPorTipoPorPrenda[it.prendaId][tipo] += cant;

      // Precio aplicado (con recargo si hay)
      const precioUnit = Number(it.precioUnitario || 0);
      subtPorTipoPorPrenda[it.prendaId][tipo] += cant * precioUnit;
    });
  });
  const prendasArr = Array.from(prendasMap.values()).sort((a, b) =>
    a.nombre.localeCompare(b.nombre),
  );

  // Subtotales generales por tipo
  const subtotalesPorTipo = { ordinaria: { cantidad: 0, subtotal: 0 }, dominical: { cantidad: 0, subtotal: 0 }, extra: { cantidad: 0, subtotal: 0 } };
  Object.keys(cantPorTipoPorPrenda).forEach((prendaId) => {
    Object.entries(cantPorTipoPorPrenda[prendaId]).forEach(([tipo, cant]) => {
      subtotalesPorTipo[tipo].cantidad += cant;
      subtotalesPorTipo[tipo].subtotal += subtPorTipoPorPrenda[prendaId][tipo];
    });
  });
  const tiposPresentes = Object.entries(subtotalesPorTipo)
    .filter(([_, v]) => v.cantidad > 0)
    .map(([k]) => k);
  const hayRecargo = tiposPresentes.some((t) => t === 'dominical' || t === 'extra');
  const granTotalCantidad = prendasArr.reduce((s, p) => s + p.cantidad, 0);
  const totalGeneral = Number(liquidacion.total || 0);

  // ─── Helpers de estilo ─────────────────────────────────────
  // Las celdas en SheetJS Community no soportan background, pero sí
  // formato numérico y merges. Usamos celdas "vacías" con merges para
  // simular separadores y texto enriquecido vía bold (negrita) en valores.
  const setFmt = (ws, addr, fmt) => {
    if (ws[addr]) ws[addr].z = fmt;
  };
  const setBold = (ws, addr) => {
    if (ws[addr]) ws[addr].s = { font: { bold: true } };
  };

  // ═══════════════════════════════════════════════════════════
  // Sheet 1: Resumen
  // ═══════════════════════════════════════════════════════════
  const resumenRows = [
    [],
    ['LIQUIDACIÓN DE CLIENTE'],
    [],
    ['Cliente'],
    ['Nombre', liquidacion.cliente?.nombre || ''],
    ['NIT', liquidacion.cliente?.nit || ''],
    [],
    ['Liquidación'],
    ['Consecutivo', liquidacion.consecutivo || ''],
    ['Nombre', liquidacion.nombre || ''],
    ['Estado', liquidacion.estado || ''],
    ['Período desde', liquidacion.fechaDesde ? new Date(liquidacion.fechaDesde) : ''],
    ['Período hasta', liquidacion.fechaHasta ? new Date(liquidacion.fechaHasta) : ''],
    ['Fecha creación', liquidacion.fecha_creacion ? new Date(liquidacion.fecha_creacion) : ''],
    ['Fecha liquidación', liquidacion.fecha_liquidacion ? new Date(liquidacion.fecha_liquidacion) : ''],
    ['Liquidada por', liquidacion.liquidada_por?.nombre || ''],
    ['Creada por', liquidacion.creado_por?.nombre || ''],
    [],
    ['Tipos de entrega incluidos', tiposPresentes.map((t) => TIPO_LABEL[t]).join(' · ')],
    [],
    ['Totales'],
    ['Entregas', entregas.length],
    ['Prendas (suma)', granTotalCantidad],
    ['Total', totalGeneral],
  ];
  const wsResumen = XLSX.utils.aoa_to_sheet(resumenRows);
  wsResumen['!cols'] = [{ wch: 22 }, { wch: 30 }];
  wsResumen['!merges'] = [
    { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }, // título principal
  ];
  // Formato a celdas con fechas y moneda
  ['B12', 'B13', 'B14', 'B15'].forEach((a) => setFmt(wsResumen, a, FMT_DATE));
  setFmt(wsResumen, 'B23', FMT_MONEY);
  setBold(wsResumen, 'A1');
  setBold(wsResumen, 'A2');
  setBold(wsResumen, 'A4');
  setBold(wsResumen, 'A8');
  setBold(wsResumen, 'A21');

  // ═══════════════════════════════════════════════════════════
  // Sheet 2: Kardex transaccional
  // ═══════════════════════════════════════════════════════════
  const kardexHeader = [
    'Prenda',
    ...entregas.map((e) => {
      const tipo = tipoOf(e);
      return `${TIPO_LABEL[tipo]} ${e.consecutivo} (${formatDate(e.fechaEntrega)})`;
    }),
    'Total',
  ];
  const kardexRows = [kardexHeader];
  prendasArr.forEach((p) => {
    const row = [p.nombre];
    entregas.forEach((e) => {
      const cant = cantPorEnt[p.prendaId]?.[e.entregaId] || 0;
      row.push(cant > 0 ? cant : '');
    });
    row.push(p.cantidad);
    kardexRows.push(row);
  });
  // Fila de totales
  const totalesRow = ['TOTAL'];
  entregas.forEach((e) => {
    const tot = prendasArr.reduce((s, p) => s + (cantPorEnt[p.prendaId]?.[e.entregaId] || 0), 0);
    totalesRow.push(tot > 0 ? tot : '');
  });
  totalesRow.push(granTotalCantidad);
  kardexRows.push(totalesRow);

  const wsKardex = XLSX.utils.aoa_to_sheet(kardexRows);
  wsKardex['!cols'] = [
    { wch: 28 },
    ...entregas.map(() => ({ wch: 20 })),
    { wch: 10 },
  ];
  // Congelar primera columna
  wsKardex['!freeze'] = { xSplit: 1, ySplit: 1 };

  // ═══════════════════════════════════════════════════════════
  // Sheet 3: Precios y Subtotales (bloques por tipo)
  // ═══════════════════════════════════════════════════════════
  // Layout (11 columnas, 2 filas de header):
  //
  //   A         B    C    D       E   F   G       H   I   J      K
  //   ┌───────┬──────────────┬──────────────┬──────────────┬──────┐
  //   │Prenda │  Ordinarias  │    Extras    │ Dominicales  │Total │
  //   │       │ Cant|$|Sub  │ Cant|$|Sub  │ Cant|$|Sub  │      │
  //   ├───────┼─────┼──┼───┼─────┼──┼───┼─────┼──┼───┼──────┤
  //   │ ...   │  30 │1000|30k │  —  │— │ —  │  20 │1200|24k  │ 54k  │
  //   └───────┴────────────────┴──────────────┴──────────────┴──────┘

  // Header fila 0 (etiquetas de tipo con colspan)
  const headerRow0 = ['', 'Ordinarias', '', '', 'Extras', '', '', 'Dominicales', '', '', 'Total'];
  // Header fila 1 (sub-etiquetas Cant / Precio / Subtotal por tipo)
  const headerRow1 = [
    'Prenda',
    // Ordinarias
    'Cant', 'Precio', 'Subtotal',
    // Extras
    'Cant', 'Precio', 'Subtotal',
    // Dominicales
    'Cant', 'Precio', 'Subtotal',
    'Total',
  ];
  const preciosRows = [headerRow0, headerRow1];

  // Body: una fila por prenda
  prendasArr.forEach((p) => {
    // Para cada tipo: cant, precio aplicado (= subtotal / cant), subtotal
    const cellByTipo = {};
    (['ordinaria', 'extra', 'dominical']).forEach((tipo) => {
      const cant = cantPorTipoPorPrenda[p.prendaId]?.[tipo] || 0;
      const sub = subtPorTipoPorPrenda[p.prendaId]?.[tipo] || 0;
      const precio = cant > 0 ? sub / cant : 0;
      cellByTipo[tipo] = { cant, sub, precio };
    });
    // Calcular total de la prenda (suma de subtotales por tipo)
    const totalPrenda =
      cellByTipo.ordinaria.sub + cellByTipo.extra.sub + cellByTipo.dominical.sub;
    const totalCantPrenda = p.cantidad;

    const row = [p.nombre];
    (['ordinaria', 'extra', 'dominical']).forEach((tipo) => {
      const { cant, sub, precio } = cellByTipo[tipo];
      row.push(cant > 0 ? cant : '');
      row.push(cant > 0 ? precio : '');
      row.push(sub > 0 ? sub : '');
    });
    row.push(totalCantPrenda, totalPrenda);
    preciosRows.push(row);
  });

  // Footer: Subtotales por tipo
  const subRow = ['Subtotales'];
  (['ordinaria', 'extra', 'dominical']).forEach((tipo) => {
    const d = subtotalesPorTipo[tipo];
    subRow.push(d.cantidad > 0 ? d.cantidad : '');
    subRow.push('');
    subRow.push(d.subtotal > 0 ? d.subtotal : '');
  });
  subRow.push(granTotalCantidad, totalGeneral);
  preciosRows.push(subRow);

  // Footer: TOTAL
  const totalRow = ['TOTAL'];
  (['ordinaria', 'extra', 'dominical']).forEach((tipo) => {
    const d = subtotalesPorTipo[tipo];
    totalRow.push(d.cantidad > 0 ? d.cantidad : '');
    totalRow.push('');
    totalRow.push(d.subtotal > 0 ? d.subtotal : '');
  });
  totalRow.push(granTotalCantidad, totalGeneral);
  preciosRows.push(totalRow);

  const wsPrecios = XLSX.utils.aoa_to_sheet(preciosRows);
  wsPrecios['!cols'] = [
    { wch: 28 }, // Prenda
    { wch: 10 }, { wch: 12 }, { wch: 14 }, // Ord: Cant, Precio, Subtotal
    { wch: 10 }, { wch: 12 }, { wch: 14 }, // Extra
    { wch: 10 }, { wch: 12 }, { wch: 14 }, // Dom
    { wch: 14 }, // Total
  ];
  wsPrecios['!freeze'] = { xSplit: 1, ySplit: 2 };
  // Merges: Prenda rowspan 2 (A1:A2), Total rowspan 2 (K1:K2)
  // y los 3 headers de tipo con colspan
  wsPrecios['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // Prenda rowspan=2
    { s: { r: 0, c: 10 }, e: { r: 1, c: 10 } }, // Total rowspan=2
    { s: { r: 0, c: 1 }, e: { r: 0, c: 3 } }, // Ordinarias
    { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } }, // Extras
    { s: { r: 0, c: 7 }, e: { r: 0, c: 9 } }, // Dominicales
  ];

  // Formato numérico a columnas de precio y subtotal
  // Filas: 2 (header) + N prendas + 2 (footer)
  for (let r = 2; r < preciosRows.length; r++) {
    const rowIdx = r + 1; // 1-based
    // Cant (B, E, H): columnas 2, 5, 8 (1-based) = B, E, H
    setFmt(wsPrecios, `B${rowIdx}`, FMT_INT);
    setFmt(wsPrecios, `E${rowIdx}`, FMT_INT);
    setFmt(wsPrecios, `H${rowIdx}`, FMT_INT);
    // Precio (C, F, I)
    setFmt(wsPrecios, `C${rowIdx}`, FMT_MONEY);
    setFmt(wsPrecios, `F${rowIdx}`, FMT_MONEY);
    setFmt(wsPrecios, `I${rowIdx}`, FMT_MONEY);
    // Subtotal (D, G, J)
    setFmt(wsPrecios, `D${rowIdx}`, FMT_MONEY);
    setFmt(wsPrecios, `G${rowIdx}`, FMT_MONEY);
    setFmt(wsPrecios, `J${rowIdx}`, FMT_MONEY);
    // Total (K)
    setFmt(wsPrecios, `K${rowIdx}`, FMT_INT); // K1=header (texto), K2=header (texto), K3.. son números
    setFmt(wsPrecios, `K${rowIdx}`, FMT_MONEY);
  }

  // ═══════════════════════════════════════════════════════════
  // Sheet 4: Detalle de Entregas (auditoría con desglose)
  // ═══════════════════════════════════════════════════════════
  const detalleHeader = [
    'Consecutivo',
    'Fecha entrega',
    'Tipo entrega',
    'Lavandería',
    'Prenda',
    'Cantidad',
    'Precio base',
    '% Apl.',
    'Recargo unitario',
    'Precio final',
    'Subtotal',
  ];
  const detalleRows = [detalleHeader];
  entregas.forEach((e) => {
    (e.items || []).forEach((it) => {
      const base = Number(it.precioBase || 0);
      const pct = Number(it.porcentaje || 100);
      const rec = Number(it.recargo || 0);
      const prec = Number(it.precioUnitario || 0);
      detalleRows.push([
        e.consecutivo,
        e.fechaEntrega ? new Date(e.fechaEntrega) : '',
        TIPO_LABEL[tipoOf(e)] || 'Ord',
        e.lavanderia?.nombre || '',
        it.nombre,
        it.cantidad,
        base,
        hayRecargo ? pct / 100 : '',
        rec,
        prec,
        it.subtotal || 0,
      ]);
    });
  });
  const wsDetalle = XLSX.utils.aoa_to_sheet(detalleRows);
  wsDetalle['!cols'] = [
    { wch: 14 }, // consecutivo
    { wch: 12 }, // fecha
    { wch: 12 }, // tipo
    { wch: 22 }, // lavandería
    { wch: 28 }, // prenda
    { wch: 10 }, // cantidad
    { wch: 14 }, // precio base
    { wch: 10 }, // %
    { wch: 14 }, // recargo
    { wch: 14 }, // precio final
    { wch: 18 }, // subtotal
  ];
  wsDetalle['!freeze'] = { xSplit: 0, ySplit: 1 };
  // Formato de moneda a columnas
  const totalDetRows = detalleRows.length;
  for (let r = 1; r < totalDetRows; r++) {
    setFmt(wsDetalle, `B${r + 1}`, FMT_DATE);
    setFmt(wsDetalle, `G${r + 1}`, FMT_MONEY);
    setFmt(wsDetalle, `I${r + 1}`, FMT_MONEY);
    setFmt(wsDetalle, `J${r + 1}`, FMT_MONEY);
    setFmt(wsDetalle, `K${r + 1}`, FMT_MONEY);
  }

  // ═══════════════════════════════════════════════════════════
  // Generar y descargar
  // ═══════════════════════════════════════════════════════════
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');
  XLSX.utils.book_append_sheet(wb, wsKardex, 'Kardex');
  XLSX.utils.book_append_sheet(wb, wsPrecios, 'Precios y Subtotales');
  XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle de Entregas');

  const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const clienteSlug = (liquidacion.cliente?.nombre || 'cliente')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  const filename = `liquidacion_${liquidacion.consecutivo || 's-n'}_${clienteSlug}_${fecha}.xlsx`;
  XLSX.writeFile(wb, filename);
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' });
};
